import * as RT from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';
import { cn } from './cn';

export interface TooltipProps {
  label?: ReactNode;
  /** antd alias for `label`. */
  title?: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** antd placement (top/bottom/left/right + corner variants) — mapped to a side. */
  placement?: string;
  className?: string;
  /** Hover delay before showing (ms). */
  delay?: number;
  /** Make the trigger fill its container (block, full width) instead of shrink-wrapping —
   *  needed when the tooltip wraps full-width content like a progress bar in a table cell. */
  block?: boolean;
}

const SIDE_OF: Record<string, 'top' | 'bottom' | 'left' | 'right'> = {
  top: 'top', topLeft: 'top', topRight: 'top',
  bottom: 'bottom', bottomLeft: 'bottom', bottomRight: 'bottom',
  left: 'left', leftTop: 'left', leftBottom: 'left',
  right: 'right', rightTop: 'right', rightBottom: 'right',
};

/**
 * Hover / focus tooltip (replaces antd Tooltip). Built on Radix Tooltip: it PORTALS
 * to the body and is collision-aware (auto-shifts/flips to stay on screen), so it
 * never gets clipped by an `overflow` scroll container or the viewport edge — e.g. a
 * delete button at the right edge of a table row. Accepts `label` (or antd `title`).
 */
export function Tooltip({ label, title, children, side = 'top', placement, className, delay = 150, block }: TooltipProps) {
  const content = label ?? title;
  if (content == null || content === '') return <>{children}</>;
  const resolvedSide = placement ? (SIDE_OF[placement] ?? 'top') : side;
  return (
    <RT.Provider delayDuration={delay} disableHoverableContent>
      <RT.Root>
        <RT.Trigger asChild>
          <span className={block ? 'block w-full' : 'inline-flex'}>{children}</span>
        </RT.Trigger>
        <RT.Portal>
          <RT.Content
            side={resolvedSide}
            sideOffset={6}
            collisionPadding={8}
            className={cn(
              'z-[var(--z-tooltip)] max-w-xs rounded-md bg-[#1f2a22] px-2 py-1 text-xs font-medium leading-snug text-white shadow-pop',
              'select-none data-[state=delayed-open]:animate-fade-in data-[state=instant-open]:animate-fade-in',
              className,
            )}
          >
            {content}
            <RT.Arrow className="fill-[#1f2a22]" width={11} height={5} />
          </RT.Content>
        </RT.Portal>
      </RT.Root>
    </RT.Provider>
  );
}
